import React, { useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  setManagedKeyData,
  clearManagedKeyData,
  setManagedKeyPending,
  setSnowflakeAccountId,
  completeStep,
  selectManagedKeyData,
  selectIsAccountValid,
  selectHasSqlCompleted,
  selectShowSqlSection,
  selectCachedAccountIds,
  setSqlConfirmation,
  clearAccountValidation,
  setCachedAccountId,
  clearCachedAccountId,
  setShowSqlSection,
  setAccountValid,
} from '../reducers/onboardingSlice';
import { snowflakeApi, getSnowflakeErrorMessage } from '../services/snowflakeApi';
import { InitialSetupView } from './InitialSetupView';
import { SQLSetupView } from './SQLSetupView';

/**
 * ManagedKeyForm Component
 *
 * Form for managed key-based Snowflake authentication using progressive disclosure.
 *
 * Flow:
 * 1. Account identifier input with validation
 * 2. "Next" button appears when validation succeeds
 * 3. After "Next", create account and reveal SQL generation section
 * 4. SQL section includes unified SQL, explanation toggle, Snowsight button, confirmation checkbox
 * 5. Test Connection enabled only when "I have run this SQL" is checked
 *
 * @component
 * @example
 * ```tsx
 * <ManagedKeyForm />
 * ```
 */
const ManagedKeyForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const managedKeyData = useAppSelector(selectManagedKeyData);
  const isAccountValid = useAppSelector(selectIsAccountValid);
  const hasSqlCompleted = useAppSelector(selectHasSqlCompleted);
  const showSqlSection = useAppSelector(selectShowSqlSection);
  const cachedAccountIds = useAppSelector(selectCachedAccountIds);
  const posthog = usePostHog();

  // Form state
  const [accountIdentifier, setAccountIdentifier] = useState<string>('');
  const [username] = useState<string>('DATAOPS_ADMIN_USER');
  const [warehouse] = useState<string>('DATAOPS_ADMIN_WAREHOUSE');
  const [role] = useState<string>('DATAOPS_ADMIN_ROLE');

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState<boolean>(false);

  /**
   * State restoration: Fetch account details when cached ID exists in Redux
   * This handles navigation scenarios (auth method switching, page reload, etc.)
   */
  useEffect(() => {
    const cachedAccountId = cachedAccountIds.managed_key;
    if (cachedAccountId !== null) {
      // Fetch account details from backend
      const fetchAccount = async (): Promise<void> => {
        try {
          const account = await snowflakeApi.getManagedKeyAccount(cachedAccountId);

          // Restore form state
          setAccountIdentifier(account.account);

          // Restore Redux state
          dispatch(
            setManagedKeyData({
              publicKey: account.public_key,
              fingerprint: account.public_key_fingerprint,
              accountId: account.account_id,
              isPending: false,
            }),
          );
          dispatch(setSnowflakeAccountId(account.account_id));
          dispatch(setShowSqlSection(true));
          dispatch(setAccountValid());
        } catch (err: unknown) {
          // Account no longer exists or user doesn't have access
          console.error('Failed to restore cached account:', err);
          dispatch(clearCachedAccountId('managed_key'));
          dispatch(setShowSqlSection(false));
          dispatch(clearManagedKeyData());
        }
      };

      void fetchAccount();
    }
  }, [cachedAccountIds.managed_key, dispatch]);

  /**
   * Generates complete SQL block with all setup commands
   */
  const generateCompleteSqlBlock = (account: {
    username: string;
    role: string;
    warehouse: string;
    publicKey: string;
  }): string => {
    return `-- Create role
USE ROLE SECURITYADMIN;
CREATE ROLE IF NOT EXISTS ${account.role} COMMENT = 'DataOps admin role';

-- Grant required account-level privileges
USE ROLE ACCOUNTADMIN;
GRANT
    CREATE DATABASE,
    CREATE USER,
    CREATE ROLE,
    CREATE WAREHOUSE,
    CREATE SHARE,
    CREATE INTEGRATION,
    CREATE NETWORK POLICY,
    MANAGE GRANTS
ON ACCOUNT TO ROLE ${account.role};

-- (Optional) Make it manageable via your org hierarchy
GRANT ROLE ${account.role} TO ROLE SYSADMIN;

-- Create a small, resumable warehouse for DataOps admin tasks
CREATE WAREHOUSE IF NOT EXISTS ${account.warehouse}
    WITH WAREHOUSE_SIZE = 'X-SMALL'
         AUTO_SUSPEND = 60
         AUTO_RESUME = TRUE
         INITIALLY_SUSPENDED = TRUE
         COMMENT = 'DataOps admin warehouse';

-- Grant minimal access to the DataOps admin role
GRANT USAGE, OPERATE, MONITOR
    ON WAREHOUSE ${account.warehouse}
    TO ROLE ${account.role};

-- Create a dedicated service user (recommended)
USE ROLE USERADMIN;
CREATE USER IF NOT EXISTS ${account.username}
    TYPE = SERVICE
    DEFAULT_WAREHOUSE = ${account.warehouse}
    DEFAULT_ROLE = ${account.role}
    DISPLAY_NAME = 'DataOps Admin User';

USE ROLE SECURITYADMIN;
GRANT ROLE ${account.role} TO USER ${account.username};

-- Add the public key for authentication
ALTER USER ${account.username} SET RSA_PUBLIC_KEY='${account.publicKey}';`;
  };

  /**
   * Handles Next button click - creates account and generates keys
   * Includes duplicate prevention check
   */
  const handleNext = async (): Promise<void> => {
    if (!isAccountValid) {
      setError('Please wait for account validation to complete');
      return;
    }

    // Check for existing cached account (duplicate prevention)
    const cachedAccountId = managedKeyData?.accountId;
    if (cachedAccountId !== null && cachedAccountId !== undefined) {
      // Account already exists, just show SQL section
      dispatch(setShowSqlSection(true));
      return;
    }

    setLoading(true);
    setError(null);
    dispatch(setManagedKeyPending(true));

    try {
      const response = await snowflakeApi.createManagedKeyAccount({
        type: 'managed_pkey',
        account: accountIdentifier,
        username,
        warehouse,
        role,
      });

      const accountId = response.account_id;
      const publicKeyValue = response.public_key;
      const fingerprint = response.public_key_fingerprint;

      // Store managed key data in Redux
      dispatch(
        setManagedKeyData({
          publicKey: publicKeyValue,
          fingerprint,
          accountId,
          isPending: false,
        }),
      );

      dispatch(setSnowflakeAccountId(accountId));

      // Cache account ID in Redux
      dispatch(setCachedAccountId({ authType: 'managed_key', accountId }));

      // Show SQL section
      dispatch(setShowSqlSection(true));
      setLoading(false);

      // Track successful account creation
      posthog?.capture('admin_app.frictionless.managed_key_account_created', {
        account_identifier: accountIdentifier,
        username,
        warehouse,
        role,
      });
    } catch (err: unknown) {
      console.error('Failed to create managed key account:', err);
      setLoading(false);
      dispatch(setManagedKeyPending(false));
      const errorMessage = getSnowflakeErrorMessage(err);
      setError(
        errorMessage !== '' ? errorMessage : 'Failed to create account. Please check your credentials and try again.',
      );

      // Track error
      posthog?.capture('admin_app.frictionless.managed_key_account_creation_failed', {
        account_identifier: accountIdentifier,
        error_message: errorMessage,
      });
    }
  };

  /**
   * Handles Edit button click - returns to account identifier input
   */
  const handleEdit = (): void => {
    dispatch(setShowSqlSection(false));
    setError(null);
    setSuccessMessage(null);
    dispatch(setSqlConfirmation(false));
    dispatch(clearAccountValidation());

    // Clear cache when changing account identifier

    dispatch(clearCachedAccountId('managed_key'));
  };

  /**
   * Tests Snowflake connection
   */
  const handleTestConnection = async (): Promise<void> => {
    const accountId = managedKeyData?.accountId;

    if (accountId === null || accountId === undefined) {
      setError('No account ID found. Please generate keys first.');
      return;
    }

    setTestingConnection(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await snowflakeApi.confirmManagedKeyAccount(accountId);

      // Connection successful - show success message
      setSuccessMessage('Connection successful. Account is now active.');
      setTestingConnection(false);

      // Clear cached account ID

      dispatch(clearCachedAccountId('managed_key'));

      // Track successful connection
      posthog?.capture('admin_app.frictionless.managed_key_connection_successful', {
        account_identifier: accountIdentifier,
        username,
      });

      // Complete the step after a brief delay to show success message
      setTimeout(() => {
        dispatch(completeStep('snowflake_setup'));
      }, 1500);
    } catch (err: unknown) {
      console.error('Connection test failed:', err);
      setTestingConnection(false);
      const errorMessage = getSnowflakeErrorMessage(err);
      const displayError =
        errorMessage !== ''
          ? errorMessage
          : 'Connection test failed. Please verify the SQL was run correctly in Snowflake.';
      setError(displayError);

      // Track connection test failure
      posthog?.capture('admin_app.frictionless.connection_test_failed', {
        account_identifier: accountIdentifier,
        username,
        error_message: errorMessage,
      });
    }
  };

  /**
   * Render initial state - account identifier input only
   * Also show this view if SQL section is flagged but we don't have account data yet (loading state)
   */
  if (!showSqlSection || (showSqlSection && managedKeyData === null)) {
    return (
      <InitialSetupView
        accountIdentifier={accountIdentifier}
        onAccountIdentifierChange={setAccountIdentifier}
        loading={loading}
        error={error}
        isAccountValid={isAccountValid}
        onNext={() => {
          void handleNext();
        }}
      />
    );
  }

  /**
   * Render SQL generation section - shown after account creation
   */
  const publicKey = managedKeyData?.publicKey ?? '';

  const completeSql = generateCompleteSqlBlock({
    username,
    role,
    warehouse,
    publicKey,
  });

  return (
    <SQLSetupView
      accountIdentifier={accountIdentifier}
      completeSql={completeSql}
      error={error}
      successMessage={successMessage}
      testingConnection={testingConnection}
      hasSqlCompleted={hasSqlCompleted}
      onEdit={handleEdit}
      onTestConnection={() => {
        void handleTestConnection();
      }}
    />
  );
};

export default ManagedKeyForm;
